import { MAX_SELECTION_DAYS_ALLOWED, ITripDriver } from '../../services/trips-list';
import { ELD_MODES } from '../eld-modes';

export enum RESTRICTION_REASON {
  'NEXT_DAY_LOG_NOT_PRESENT', 'DRIVING_PERIOD_IN_PROGRESS', 'CO_DRIVERS_NOT_PRESENT',
  'MULTIPLE_LOG_SUGGESTIONS', 'PENDING_LOG_SUGGESTION', 'CURRENT_DAY_DRIVING_PERIOD',
  'EXEMPT_DRIVING_PERIOD',
  // for bulk assign & annotate
  'SDS_EVENT_SELECTED', 'OVERLAPPING_TRIPS_SELECTED', 'MAXIMUM_SELECTED_DAYS_RANGE_EXCEEDED',
  'LOG_AND_EXEMPTED_TRIPS_SELECTED', 'EXEMPT_DRIVING_PERIOD_NOT_ALLOWED_FOR_ANNOTATE', 'PENDING_LOG_SUGGESTION_EXEMPT',
  'SPLIT_DRIVING_PERIOD', 'REGULATION_MODE_MISMATCH', 'RESTRICT_UNREGULATED_TRIPS_BULK_ANNOTATION', 'RESTRICT_UNREGULATED_TRIPS_ANNOTATION',
  'RESTRICT_BULK_UNREGULATED_AG_TRIPS_ANNOTATION', 'RESTRICT_UNREGULATED_AG_TRIPS_ANNOTATION', 'OEM_ENTITY',
}

export const RESTRICTION_REASON_MAPPING = {
  next_day_log_not_present: RESTRICTION_REASON.NEXT_DAY_LOG_NOT_PRESENT,
  driving_period_in_progress: RESTRICTION_REASON.DRIVING_PERIOD_IN_PROGRESS,
  co_drivers_not_present: RESTRICTION_REASON.CO_DRIVERS_NOT_PRESENT,
  multiple_log_suggestions: RESTRICTION_REASON.MULTIPLE_LOG_SUGGESTIONS,
  pending_log_suggestion: RESTRICTION_REASON.PENDING_LOG_SUGGESTION,
  current_day_driving_period: RESTRICTION_REASON.CURRENT_DAY_DRIVING_PERIOD,
  exempt_driving_period: RESTRICTION_REASON.EXEMPT_DRIVING_PERIOD,
  oem_entity: RESTRICTION_REASON.OEM_ENTITY,
  // for bulk assign & annotate
  sds_event_selected: RESTRICTION_REASON.SDS_EVENT_SELECTED,
  overlappig_trips_selected: RESTRICTION_REASON.OVERLAPPING_TRIPS_SELECTED,
  maximum_selected_days_range_exceeded: RESTRICTION_REASON.MAXIMUM_SELECTED_DAYS_RANGE_EXCEEDED,
  logs_and_exempted_trips_selected: RESTRICTION_REASON.LOG_AND_EXEMPTED_TRIPS_SELECTED,
  exempt_driving_period_not_allowed_for_annotate: RESTRICTION_REASON.EXEMPT_DRIVING_PERIOD_NOT_ALLOWED_FOR_ANNOTATE,
  split_driving_period: RESTRICTION_REASON.SPLIT_DRIVING_PERIOD,
};

export function mapRestrictionReason(restrictionReason): RESTRICTION_REASON {
  if (!restrictionReason) return null;
  else return RESTRICTION_REASON_MAPPING[restrictionReason];
}

export function generateToolTipText(
  restrictionReason: RESTRICTION_REASON,
  driverFirstName: string = '',
  logSuggestion = null,
  drivingPeriod = null,
  isBulkAssignTrip: boolean = false): string {
  const multipleDrivers = logSuggestionParam => logSuggestionParam && logSuggestionParam.drivers.length > 1;
  if (restrictionReason === RESTRICTION_REASON.NEXT_DAY_LOG_NOT_PRESENT) {
    return $localize`This trip cannot be edited because ${driverFirstName} does not have a log for the next day`;
  }
  if (restrictionReason === RESTRICTION_REASON.DRIVING_PERIOD_IN_PROGRESS) {
    return $localize`This trip cannot be edited because it is currently in progress`;
  }
  if (restrictionReason === RESTRICTION_REASON.OEM_ENTITY) {
    return $localize`This trip cannot be edited because it is a Ford Pro integrated vehicle`;
  }
  if (restrictionReason === RESTRICTION_REASON.CO_DRIVERS_NOT_PRESENT) {
    return $localize`This trip cannot be reassigned because there are no co-drivers on ${driverFirstName}’s log`;
  }
  if (restrictionReason === RESTRICTION_REASON.MULTIPLE_LOG_SUGGESTIONS) {
    return $localize`This trip cannot be edited right now`;
  }

  if (restrictionReason === RESTRICTION_REASON.CURRENT_DAY_DRIVING_PERIOD) {
    return $localize`This trip cannot be assigned or annotated yet because there are no drivers with a log for the next day.`;
  }

  if (restrictionReason === RESTRICTION_REASON.EXEMPT_DRIVING_PERIOD) {
    return $localize`This trip cannot be edited because the driver did not require logs for that day`;
  }

  if (restrictionReason === RESTRICTION_REASON.PENDING_LOG_SUGGESTION) {
    let driverNamesString;
    const extractNonExemptedDriversName = drivers => {
      return drivers.filter(driver => driver.eldMode !== ELD_MODES.EXEMPTED).map(item => item.firstName);
    };

    if (multipleDrivers(logSuggestion)) {
      driverNamesString = concatenateDriverNames(extractNonExemptedDriversName(logSuggestion.drivers));
    } else if (!isBulkAssignTrip) driverNamesString = driverFirstName || drivingPeriod.receivingLog.driver.firstName;

    // In case of bulk assign if we reassign a trip to a driver other than current driver then we will have to fetch
    // suggested log info of driver from changedAttributes field of tripList to fetch info of reassign driver.
    if (isBulkAssignTrip && drivingPeriod) {
      if (drivingPeriod.changedAttributes && drivingPeriod.changedAttributes.driver) {
        const drivers = [];
        const targetDriver: ITripDriver = drivingPeriod.changedAttributes.driver;

        if (drivingPeriod.driver.firstName) drivers.push(drivingPeriod.driver);
        if (targetDriver) drivers.push(targetDriver);
        driverNamesString = concatenateDriverNames(extractNonExemptedDriversName(drivers));
      }
    }

    // tslint:disable-next-line: max-line-length
    return $localize`${driverNamesString} must approve or reject the pending edit on their log before you can edit this trip`;
  }

  if (restrictionReason === RESTRICTION_REASON.PENDING_LOG_SUGGESTION_EXEMPT) {
    // tslint:disable-next-line: max-line-length
    return $localize`The previous log suggestion related to ${driverFirstName} must be approved before you can edit this trip`;
  }

  // for bulk assign & annotate
  if (restrictionReason === RESTRICTION_REASON.SDS_EVENT_SELECTED) {
    return $localize`Special Driving Status trips cannot be reassigned`;
  }

  if (restrictionReason === RESTRICTION_REASON.OVERLAPPING_TRIPS_SELECTED) {
    return $localize`Overlapping trips cannot be assigned`;
  }

  if (restrictionReason === RESTRICTION_REASON.MAXIMUM_SELECTED_DAYS_RANGE_EXCEEDED) {
    // tslint:disable-next-line: max-line-length
    return $localize`To select multiple trips, limit date range to a ${MAX_SELECTION_DAYS_ALLOWED} day period`;
  }

  if (restrictionReason === RESTRICTION_REASON.LOG_AND_EXEMPTED_TRIPS_SELECTED) {
    return $localize`Identified trips cannot be assigned between both logs required drivers and exempt drivers`;
  }

  if (restrictionReason === RESTRICTION_REASON.EXEMPT_DRIVING_PERIOD_NOT_ALLOWED_FOR_ANNOTATE) {
    return $localize`Notes cannot be updated once the trip is assigned to a driver who does not require logs`;
  }

  if (restrictionReason === RESTRICTION_REASON.SPLIT_DRIVING_PERIOD) {
    return $localize`Split Special Driving Status Cannot be Reassigned`;
  }

  if (restrictionReason === RESTRICTION_REASON.REGULATION_MODE_MISMATCH) {
    return $localize`Bulk trip assignment is not possible when you select a mix of regulated and unregulated vehicles`;
  }

  if (restrictionReason === RESTRICTION_REASON.RESTRICT_UNREGULATED_TRIPS_BULK_ANNOTATION) {
    return $localize`Bulk annotation is not available for trips involving unregulated vehicles.`;
  }

  if (restrictionReason === RESTRICTION_REASON.RESTRICT_BULK_UNREGULATED_AG_TRIPS_ANNOTATION) {
    return $localize`Bulk annotation is not possible when you select a mix of trips recorded inside and outside of agriculture exempt radius.`;
  }

  if (restrictionReason === RESTRICTION_REASON.RESTRICT_UNREGULATED_TRIPS_ANNOTATION) {
    return $localize`Annotation is not available for trips involving unregulated vehicles.`;
  }

  if (restrictionReason === RESTRICTION_REASON.RESTRICT_UNREGULATED_AG_TRIPS_ANNOTATION) {
    return $localize`Annotation is non-editable for unregulated trips recorded under agriculture exempt radius.`;
  }
}

function concatenateDriverNames(names: string[]): string {
  if (names.length === 1) return names[0];
  const lastIndex = names.length - 1;
  const secondLastIndex = names.length - 2;
  return names.reduce((concatenatedNames, name, index) => {
    if (index === lastIndex) return `${concatenatedNames} and ${name}`;
    else {
      concatenatedNames += name;
      if (index !== secondLastIndex) concatenatedNames += ', ';
      return concatenatedNames;
    }
  }, '');
}
